import React, { useState, useEffect } from 'react';
import { Search, Grid, List, ExternalLink } from 'lucide-react';

interface CaseStudy {
  id: string;
  title: string;
  category: string;
  icon: string;
  description: string;
  tags: string[];
  benefits: string[];
  filter: string;
  route: string;
  component: string;
  featured?: boolean;
  difficulty?: string;
  timeToImplement?: string;
  industries?: string[];
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface HomepageData {
  title: string;
  subtitle: string;
  caseStudies: CaseStudy[];
  filters: FilterOption[];
  featuredSection: {
    title: string;
    subtitle: string;
  };
  stats: {
    totalCaseStudies: number;
    averageImplementationTime: string;
    successRate: string;
    industriesCovered: number;
  };
}

const HomePage: React.FC = () => {
  const [homepageData, setHomepageData] = useState<HomepageData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);

  // Load data from KV store
  useEffect(() => {
    const loadHomepageData = async () => {
      try {
        // In production: const response = await fetch('/api/homepage');
        // For now, we'll simulate the KV store data
        const mockData: HomepageData = {
          title: "AdKernel Case Studies",
          subtitle: "Real-world programmatic advertising solutions",
          caseStudies: [
            {
              id: "mobile-programmatic",
              title: "Mobile Programmatic Expansion",
              category: "Mobile Apps",
              icon: "📱",
              description: "Mobile installs company transitions to programmatic with cookieless identity targeting, Target CPA automation, and cross-platform attribution.",
              tags: ["UID2.0", "Target CPA", "MAIDs", "Attribution"],
              benefits: ["Improved Efficiency", "Enhanced Scale", "Better Attribution"],
              filter: "mobile",
              route: "/mobile-programmatic",
              component: "MobileProgrammaticCaseStudy",
              featured: true,
              difficulty: "Advanced",
              timeToImplement: "4-6 weeks"
            }
            // Add other case studies here...
          ],
          filters: [
            { id: "all", label: "All Industries", count: 8 },
            { id: "mobile", label: "Mobile Apps", count: 1 }
            // Add other filters here...
          ],
          featuredSection: {
            title: "Featured Case Studies",
            subtitle: "Our most popular implementation stories"
          },
          stats: {
            totalCaseStudies: 8,
            averageImplementationTime: "6-8 weeks",
            successRate: "95%",
            industriesCovered: 8
          }
        };
        
        setHomepageData(mockData);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load homepage data:', error);
        setLoading(false);
      }
    };

    loadHomepageData();
  }, []);

  // Filter and search logic
  const filteredCaseStudies = homepageData?.caseStudies.filter(study => {
    const matchesFilter = activeFilter === 'all' || study.filter === activeFilter;
    const matchesSearch = searchTerm === '' || 
      study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  }) || [];

  const featuredCaseStudies = homepageData?.caseStudies.filter(study => study.featured) || [];

  const CaseStudyCard: React.FC<{ study: CaseStudy; showDifficulty?: boolean }> = ({ study, showDifficulty = false }) => (
    <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 ${
      viewMode === 'list' ? 'flex items-center' : ''
    }`}>
      <div className={`${viewMode === 'list' ? 'w-24 h-24 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100' : 'p-6 pb-4'}`}>
        <span className="text-3xl">{study.icon}</span>
      </div>
      
      <div className={`${viewMode === 'list' ? 'flex-1 p-6' : 'px-6 pb-6'}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{study.title}</h3>
            <span className="inline-block px-3 py-1 text-sm font-medium text-orange-700 bg-orange-100 rounded-full">
              {study.category}
            </span>
          </div>
          {viewMode === 'grid' && (
            <ExternalLink className="w-5 h-5 text-gray-400 hover:text-orange-600 transition-colors" />
          )}
        </div>
        
        <p className="text-gray-600 mb-4 line-clamp-3">{study.description}</p>
        
        {showDifficulty && study.difficulty && (
          <div className="mb-3">
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
              study.difficulty === 'Expert' ? 'bg-red-100 text-red-700' :
              study.difficulty === 'Advanced' ? 'bg-orange-100 text-orange-700' :
              'bg-green-100 text-green-700'
            }`}>
              {study.difficulty}
            </span>
            {study.timeToImplement && (
              <span className="ml-2 text-xs text-gray-500">• {study.timeToImplement}</span>
            )}
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mb-4">
          {study.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
              {tag}
            </span>
          ))}
          {study.tags.length > 3 && (
            <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded">
              +{study.tags.length - 3} more
            </span>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {study.benefits.map((benefit, index) => (
            <span key={index} className="flex items-center text-sm text-green-700">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
              {benefit}
            </span>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-100">
          <a 
            href={study.route}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
          >
            View Case Study
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case studies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AdKernel Case Studies</h1>
              <p className="text-gray-600 mt-1">Real-world programmatic advertising solutions</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-orange-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search case studies, industries, or technologies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {homepageData?.filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeFilter === filter.id
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {filter.label}
              <span className="ml-2 text-sm opacity-75">({filter.count})</span>
            </button>
          ))}
        </div>

        {/* Stats Section */}
        {homepageData?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{homepageData.stats.totalCaseStudies}</div>
              <div className="text-sm text-gray-600">Case Studies</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{homepageData.stats.averageImplementationTime}</div>
              <div className="text-sm text-gray-600">Avg Implementation</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{homepageData.stats.successRate}</div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">{homepageData.stats.industriesCovered}</div>
              <div className="text-sm text-gray-600">Industries</div>
            </div>
          </div>
        )}

        {/* Featured Section */}
        {featuredCaseStudies.length > 0 && (
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">{homepageData?.featuredSection.title}</h2>
              <p className="text-gray-600 mt-2">{homepageData?.featuredSection.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredCaseStudies.map((study) => (
                <CaseStudyCard key={study.id} study={study} showDifficulty={true} />
              ))}
            </div>
            <div className="text-center mt-8">
              <div className="w-full h-px bg-gray-200"></div>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600">
            Showing {filteredCaseStudies.length} of {homepageData?.caseStudies.length || 0} case studies
          </p>
        </div>

        {/* Case Studies Grid/List */}
        <div className={`${
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' 
            : 'space-y-6'
        }`}>
          {filteredCaseStudies.map((study) => (
            <CaseStudyCard key={study.id} study={study} />
          ))}
        </div>

        {filteredCaseStudies.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No case studies found</h3>
              <p>Try adjusting your search terms or filters</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;